import { isMagneticNode } from '../constants/nodeTypes';
import type { Point, Size, SocketDefinition } from '../types';

/** Default rim thickness for magnetic connect. Scaled down on small nodes. */
export const MAGNETIC_BORDER_PX = 8;
/** Ignore a socket if the pointer is farther than this from it. */
export const MAGNETIC_SOCKET_SNAP_PX = 18;

export interface MagneticNode {
  id: string;
  type: string;
  position: Point;
  size: Size;
  sockets: SocketDefinition[];
}

/** Rim width that still leaves a clickable interior on small nodes. */
export function magneticBorderThreshold(width: number, height: number): number {
  const shortest = Math.min(width, height);
  return Math.max(5, Math.min(MAGNETIC_BORDER_PX, Math.round(shortest * 0.12)));
}

/** True when the local pointer is on the rim of a node (not the interior). */
export function isNearNodeBorder(
  localX: number,
  localY: number,
  width: number,
  height: number,
  threshold: number = MAGNETIC_BORDER_PX,
): boolean {
  if (localX < -4 || localY < -4 || localX > width + 4 || localY > height + 4) return false;
  return localX <= threshold || localY <= threshold || localX >= width - threshold || localY >= height - threshold;
}

function socketWorld(node: MagneticNode, socket: SocketDefinition): Point {
  return {
    x: node.position.x + socket.position.x,
    y: node.position.y + socket.position.y,
  };
}

function dist2(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function nearestSocket(
  node: MagneticNode,
  canvasX: number,
  canvasY: number,
  sockets: SocketDefinition[] = node.sockets,
  maxDistPx?: number,
): SocketDefinition | null {
  if (sockets.length === 0) return null;
  const target = { x: canvasX, y: canvasY };
  let best: SocketDefinition | null = null;
  let bestD = Infinity;
  for (const socket of sockets) {
    const d = dist2(socketWorld(node, socket), target);
    if (d < bestD) {
      bestD = d;
      best = socket;
    }
  }
  if (best && maxDistPx !== undefined && bestD > maxDistPx * maxDistPx) return null;
  return best;
}

/**
 * Pick the attachment point on `target` for a drag that started at `sourceSocket`.
 * Drawing shapes: nearest socket of any polarity (drag order sets direction).
 * Architecture nodes: prefer the opposite polarity so stored edges stay output → input.
 */
export function pickMagneticTargetSocket(
  source: MagneticNode,
  sourceSocket: SocketDefinition,
  target: MagneticNode,
): SocketDefinition | null {
  if (target.sockets.length === 0) return null;
  const srcWorld = socketWorld(source, sourceSocket);
  if (isMagneticNode(target.type)) {
    return nearestSocket(target, srcWorld.x, srcWorld.y);
  }
  // Dragging out of a drawing shape is always an output, so land on an input.
  if (isMagneticNode(source.type)) {
    const inputs = target.sockets.filter(s => s.type === 'input');
    const pool = inputs.length === 0 ? target.sockets : inputs;
    return nearestSocket(target, srcWorld.x, srcWorld.y, pool);
  }
  const opposite = target.sockets.filter(s => s.type !== sourceSocket.type);
  const pool = opposite.length === 0 ? target.sockets : opposite;
  return nearestSocket(target, srcWorld.x, srcWorld.y, pool);
}
